import pendingCompanionFileService from "../_services/pendingCompanionFileService";

/**
 * "Envelope Printing Upload" dropzone on the product page.
 *
 * When the customer chooses an Envelope Printing option (operation 43), the configurator
 * reveals a return-address block (`div.returnAddressMeta`) with a label reading
 * "Upload an envelope print ready file … on the next page or type your return address …"
 * and a return-address textarea. This lets them upload the envelope's print-ready artwork
 * inline instead of "on the next page": we drop a dropzone into that block and reword the
 * label so the two options (upload below / type a return address) read clearly.
 *
 * Any file type is accepted — the envelope's print-ready artwork and/or a data file. A chosen file
 * is uploaded straight away (Collaterate.Assets.stageFile), so nothing but its reservation name has
 * to survive the trip to the cart; the cart binds it to the auto-added companion envelope item as
 * that job's print file.
 *
 * The configurator regenerates the operations/return-address markup on quote updates, so the
 * panel is re-anchored and the label re-reworded on every calc/quote event (same node, its
 * listeners intact).
 */
const PANEL_ID = 'envelopePrintingUploadPanel';
// Theme tokens rather than literals, so this panel tracks the site's type and palette.
// Defined in src/_components/_vars/vars.css.
const FONT = 'var(--f-SANS)';
const ORIGINAL_LABEL_RX = /upload an envelope print[\s-]?ready file/i;
const REWORDED_LABEL = 'Upload your envelope print-ready file directly below, or type your return address in the box below.';

let panelRef = null;

/**
 * `returnAddressMeta` is reused for several envelope return-address fields (the print-ready
 * upload prompt, the return-address position, the font). Match on the label text so we only
 * ever touch the print-ready block — before OR after we reword it — never the others.
 */
function findUploadBlock () {
    const blocks = document.querySelectorAll('div.returnAddressMeta');
    for (let i = 0; i < blocks.length; i++) {
        const label = blocks[i].querySelector('label.config');
        const text = label ? label.textContent.trim() : '';
        if (ORIGINAL_LABEL_RX.test(text) || text === REWORDED_LABEL) {
            return blocks[i];
        }
    }
    return null;
}

function buildPanel () {
    const panel = document.createElement('div');
    panel.id = PANEL_ID;
    panel.style.cssText = 'margin: 12px 0 0;';
    panel.innerHTML = `
        <h3 id="envelopePrintingUploadHeading" style="display: block; font-family: ${FONT}; font-weight: normal; font-size: 15px; line-height: 18px; margin: 0 0 6px;">Envelope Printing Upload</h3>
        <label id="envelopePrintingDropzone" style="
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            border: 2px dotted var(--c-OFFBLACK-LIGHT);
            border-radius: 16px;
            background: var(--c-WHITE);
            cursor: pointer;
            padding: 12px 14px;
            font-family: ${FONT};
            font-size: 13px;
            line-height: 1.5;
            color: var(--c-BLACK);
        ">
            <span>Drag and drop, or <span style="text-decoration: underline;">browse files</span></span>
            <span id="envelopePrintingHint" style="color: var(--c-OFFBLACK); margin-top: 4px; font-size: 12px;">Envelope print-ready file or a data file — any file type</span>
            <input id="envelopePrintingInput" type="file" multiple style="display: none;">
        </label>
        <ul id="envelopePrintingFileList" style="list-style: none; margin: 10px 0 0; padding: 0; font-family: ${FONT}; font-size: 14px;"></ul>
    `;
    return panel;
}

// Names of files whose upload is still in flight, so the customer sees a row for a file the
// moment they pick it rather than only once it has finished uploading.
const uploadingNames = [];

// Built as DOM nodes rather than an innerHTML template: the file name is customer-supplied, so
// interpolating it into markup would let a crafted filename inject script. A row without a
// reservation name is still uploading and offers no remove link.
function buildFileRow (name, reservationName) {
    const row = document.createElement('li');
    row.style.cssText = 'display: flex; align-items: center; justify-content: space-between; padding: 6px 2px; border-bottom: 1px solid var(--c-OFFBLACK-LIGHTER);';

    const label = document.createElement('span');
    const fileName = document.createElement('span');
    fileName.textContent = name;
    const note = document.createElement('span');
    note.style.color = 'var(--c-OFFBLACK)';
    note.textContent = reservationName ? ' \u2014 will print on your envelope' : ' \u2014 uploading\u2026';
    label.append(fileName, note);
    row.appendChild(label);

    if (reservationName) {
        row.dataset.reservation = reservationName;
        const remove = document.createElement('a');
        remove.href = '';
        remove.className = 'js-removeEnvelopeFile';
        remove.dataset.reservation = reservationName;
        remove.style.cssText = 'color: var(--c-OFFBLACK); text-decoration: underline;';
        remove.textContent = 'remove';
        row.appendChild(remove);
    }
    return row;
}

function refreshFileList () {
    const list = document.getElementById('envelopePrintingFileList');
    if (!list) {
        return;
    }
    list.innerHTML = '';
    pendingCompanionFileService.list().forEach(record => list.appendChild(buildFileRow(record.name, record.reservationName)));
    uploadingNames.forEach(name => list.appendChild(buildFileRow(name, null)));
}

// Upload as soon as the file is chosen, while the customer is still configuring, so the cart page
// only has to bind the result to the companion instead of pushing bytes on arrival.
async function handleFiles (files) {
    const chosen = Array.from(files || []);
    if (!chosen.length) {
        return;
    }
    chosen.forEach(file => uploadingNames.push(file.name));
    refreshFileList();

    for (const file of chosen) {
        try {
            pendingCompanionFileService.add(file.name, await Collaterate.Assets.stageFile(file));
        } catch (e) {
            // drop it from the list rather than pretend it staged; the customer can pick it again
            // here, or upload from the cart's files modal
        } finally {
            const at = uploadingNames.indexOf(file.name);
            if (at !== -1) {
                uploadingNames.splice(at, 1);
            }
            refreshFileList();
        }
    }
}

function enablePanel (panel) {
    const dropzone = panel.querySelector('#envelopePrintingDropzone');
    const input = panel.querySelector('#envelopePrintingInput');

    input.addEventListener('change', e => {
        handleFiles(e.target.files);
        e.target.value = '';
    });

    ['dragenter', 'dragover'].forEach(type => dropzone.addEventListener(type, e => {
        e.preventDefault();
        dropzone.style.background = 'var(--c-OFFWHITE)';
    }));
    ['dragleave', 'drop'].forEach(type => dropzone.addEventListener(type, e => {
        e.preventDefault();
        dropzone.style.background = 'var(--c-WHITE)';
    }));
    dropzone.addEventListener('drop', e => handleFiles(e.dataTransfer && e.dataTransfer.files));

    panel.addEventListener('click', e => {
        if (e.target.matches('.js-removeEnvelopeFile')) {
            e.preventDefault();
            pendingCompanionFileService.remove(e.target.dataset.reservation);
            refreshFileList();
        }
    });
}

/**
 * Reword the stock "…on the next page…" copy to point at the dropzone below. The textarea's
 * onblur still carries the original text as the additional-field key, so the return-address
 * submission is unchanged — only the visible label is rewritten.
 */
function rewordLabel (block) {
    const label = block.querySelector('label.config');
    if (label && label.textContent.trim() !== REWORDED_LABEL) {
        label.textContent = REWORDED_LABEL;
    }
}

/**
 * (Re-)anchor the panel inside the return-address block so it shows/hides with the Envelope
 * Printing selection (the configurator toggles the block's display), and re-reword the label.
 * Runs on every calc/quote event because the configurator regenerates that markup.
 */
function ensurePanel () {
    const block = findUploadBlock();
    if (!block) {
        return; // product has no Envelope Printing print-ready field
    }
    if (!panelRef) {
        panelRef = buildPanel();
        enablePanel(panelRef);
    }
    rewordLabel(block);
    if (panelRef.parentElement !== block) {
        block.appendChild(panelRef);
    }
    refreshFileList();
}

// Both events fire AFTER the configurator has written its markup, so `ensurePanel` can run
// synchronously: `onCalcLoaded` is documented as the signal that "the markup has rendered"
// (productConfigService.js), and the return-address block was measured present at
// `onQuoteUpdated` time on every sample. No deferral needed.
if (window.configureEvents) {
    window.configureEvents.registerOnCalcLoadedListener({ onCalcLoaded: ensurePanel });
    // re-anchored on every quote update because it may have rebuilt the return-address markup
    window.configureEvents.registerOnQuoteUpdatedListener({ onQuoteUpdated: ensurePanel });
} else {
    document.addEventListener('DOMContentLoaded', ensurePanel);
}
